import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Button, Input, Popconfirm, Space, Table, TableColumnType } from "antd";
import { debounce } from "lodash";
import { useCallback, useState } from "react";
import ModalAddCustomer from "../../components/modal/ModalAddCustomer";
import ModalEditCustomer from "../../components/modal/ModalEditCustomer";
import { ICustomer, ICustomerQuery } from "../../models/Sale";
import { useDeleteCustomer, useGetCustomer } from "../../query/SaleQuery";
import { notify } from "../../utils/toast";

const Customers = () => {
  const [params, setParams] = useState<ICustomerQuery>({
    page: 1,
    limit: 10,
    search: "",
  });
  const [openModalAdd, setOpenModalAdd] = useState<boolean>(false);
  const [openModalEdit, setOpenModalEdit] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>(
    null,
  );

  const { data, isLoading } = useGetCustomer(params);
  const deleteCustomer = useDeleteCustomer();
  const handleSearchCustomer = useCallback(
    debounce((value: string) => {
      setParams((prev) => ({
        ...prev,
        search: value,
        page: 1,
      }));
    }, 500),
    [],
  );

  const handleDeleteCustomer = async (id: number) => {
    try {
      await deleteCustomer.mutateAsync({ id: id, query: params });
      notify.success("Xóa khách hàng thành công");
    } catch (error) {
      notify.error("Xóa khách hàng thất bại");
    } finally {
    }
  };

  const handleEditCustomer = (record: ICustomer) => {
    setSelectedCustomer(record);
    setOpenModalEdit(true);
  };

  const columns: TableColumnType<ICustomer>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      align: "center",
      width: 80,
    },
    {
      title: "Mã khách hàng",
      dataIndex: "code",
      key: "code",
      align: "center",
      width: 150,
    },
    {
      title: "Tên khách hàng",
      dataIndex: "name",
      key: "name",
      align: "left",
      filterDropdown: () => (
        <Input
          placeholder="Tìm kiếm theo tên"
          onChange={(e) => handleSearchCustomer(e.target.value)}
          prefix={<SearchOutlined />}
        />
      ),
    },
    {
      title: "Địa chỉ",
      dataIndex: "address",
      key: "address",
      align: "left",
    },
    {
      title: "Mã số thuế",
      dataIndex: "taxCode",
      key: "taxCode",
      align: "center",
      width: 150,
    },
    {
      title: "Hành động",
      key: "action",
      align: "center",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditCustomer(record)}
          />
          <Popconfirm
            title="Xóa khách hàng"
            description="Bạn có chắc chắn muốn xóa khách hàng này?"
            onConfirm={() => handleDeleteCustomer(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div
        style={{
          marginBottom: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setOpenModalAdd(true)}
        >
          Thêm khách hàng
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data?.data || []}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: params.page,
          pageSize: params.limit,
          total: data?.pagination.total || 0,
          onChange: (page, pageSize) => {
            setParams((prev) => ({
              ...prev,
              page,
              limit: pageSize,
            }));
          },
        }}
        scroll={{ x: 1000 }}
      />

      <ModalAddCustomer
        open={openModalAdd}
        onCancel={() => setOpenModalAdd(false)}
        onSuccess={() => {
          setOpenModalAdd(false);
        }}
      />

      <ModalEditCustomer
        open={openModalEdit}
        record={selectedCustomer}
        params={params}
        onCancel={() => {
          setOpenModalEdit(false);
          setSelectedCustomer(null);
        }}
        onSuccess={() => {
          setOpenModalEdit(false);
          setSelectedCustomer(null);
        }}
      />
    </div>
  );
};

export default Customers;
